import { throttle } from 'lodash-es';
import { useEffect } from 'preact/hooks';

import { commonStore } from '../../global-store/subjects/common';
import {
  BANNER_BOTTOM_CTA_STATUS,
  BANNER_BOTTOM_PERSONALIZATION_STATUS,
  BANNER_BOTTOM_WPN_STATUS,
  isSetOnLocalStorage,
} from '../../utils/local-storage';
import { authStore } from '../../global-store/subjects/auth';
import { getInitialState } from '../../global-store/subjects/slim-banner-bottom';
import { initialState as authInitialState } from '../../global-store/subjects/auth/initial-state';
import { initialState as commonInitialState } from '../../global-store/subjects/common/initial-state';
import { addClassToNodes, removeClassFromNodes } from '../../utils/dom-utils';
import { isStickyCarouselTemplateEnable } from '../../utils/aem-utils';

import { useSubscribe } from './use-subscribe';

const SCROLL_THROTTLE_TIME = 1000; // milliseconds value
const SHOW_BANNER_THRESHOLD = 500; // pixels value
const WITH_BANNER_CLASSNAME = 'u-with-banner-bottom';
const videoPlayerClassname = isStickyCarouselTemplateEnable() ? '.j-player-carousel-element' : '.j-player';
const VIDEO_PLAYERS = document.querySelectorAll(videoPlayerClassname);
const WPN_FAB_POPUP = document.querySelectorAll('.j-wpn-fab-popup');

const initialState = getInitialState();

const useToggleBannerBottomCommon = ({ actions, shouldCheckThreshold }) => {
  const commonState = useSubscribe(commonStore);

  const checkThreshold = throttle(() => {
    if (window.scrollY > SHOW_BANNER_THRESHOLD) {
      actions.showBannerBottom();
      window.removeEventListener('scroll', checkThreshold);
    }
  }, SCROLL_THROTTLE_TIME);

  useEffect(() => {
    if (shouldCheckThreshold) {
      window.addEventListener('scroll', checkThreshold);
      return () => window.removeEventListener('scroll', checkThreshold);
    }
  }, [shouldCheckThreshold]);

  // Hide the slim banner when the HP is injected
  useEffect(() => {
    if (commonState.isInjectedHP) {
      actions.hideBannerBottom();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commonState.isInjectedHP]);
};

export const useToggleBannerBottomPersonalization = ({ state, actions }) => {
  const authState = useSubscribe(authStore);
  const commonState = useSubscribe(commonStore);
  const isUserLoggedUnset = authState.isUserLogged === authInitialState.isUserLogged;
  const isUserLoggedInAndPreferencesUnset =
    authState.isUserLogged && state.userPreferences === initialState.userPreferences;
  const isUserLoggedInAndErrorUnset = authState.isUserLogged && state.error === initialState.error;
  const isPushNotificationShownUnset =
    commonState.isPushNotificationShown === commonInitialState.isPushNotificationShown;

  const isLoading =
    isUserLoggedUnset ||
    isUserLoggedInAndPreferencesUnset ||
    isUserLoggedInAndErrorUnset ||
    isPushNotificationShownUnset;

  const shouldCheckThreshold =
    !isLoading &&
    !(authState.isUserLogged && state.userPreferences?.length) &&
    !state.error &&
    !commonState.isPushNotificationShown &&
    !isSetOnLocalStorage(BANNER_BOTTOM_PERSONALIZATION_STATUS.OBJECT_KEY);

  useToggleBannerBottomCommon({ actions, shouldCheckThreshold });
};

export const useToggleBannerBottomCTA = ({ actions }) => {
  const commonState = useSubscribe(commonStore);

  const shouldCheckThreshold =
    !commonState.isPushNotificationShown && !isSetOnLocalStorage(BANNER_BOTTOM_CTA_STATUS.OBJECT_KEY);

  useToggleBannerBottomCommon({ actions, shouldCheckThreshold });
};

export const useToggleBannerBottomWPN = ({ pushNotificationsState, actions }) => {
  const commonState = useSubscribe(commonStore);

  const shouldCheckThreshold =
    !(Notification.permission === 'denied') &&
    pushNotificationsState.sdk?.canRegister &&
    !pushNotificationsState.sdk?.channel?.optedIn &&
    !commonState.isPushNotificationShown &&
    !isSetOnLocalStorage(BANNER_BOTTOM_WPN_STATUS.OBJECT_KEY);

  useToggleBannerBottomCommon({ actions, shouldCheckThreshold });
};

export const useWithBannerBottom = ({ state }) => {
  useEffect(() => {
    if (state.showBanner) {
      addClassToNodes({ nodes: VIDEO_PLAYERS, className: WITH_BANNER_CLASSNAME });
      addClassToNodes({ nodes: WPN_FAB_POPUP, className: WITH_BANNER_CLASSNAME });
    } else {
      removeClassFromNodes(VIDEO_PLAYERS, WITH_BANNER_CLASSNAME);
      removeClassFromNodes(WPN_FAB_POPUP, WITH_BANNER_CLASSNAME);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.showBanner]);
};
